import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { HeroEcosystem } from './HeroEcosystem';
const ease = [0.22, 1, 0.36, 1] as const;
export function Hero() {
  return <section id="inicio" className="relative w-full overflow-hidden pb-28 pt-32 sm:pb-32 sm:pt-44 lg:pb-40 lg:pt-48" data-id="element-95" data-mp="9lla0zpw82ok">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" data-id="element-96" data-mp="4jvup7vn12uo">
        <div className="absolute inset-0 grid-lines opacity-60 [mask-image:radial-gradient(120%_80%_at_50%_0%,black,transparent_75%)]" data-id="element-97" data-mp="128phrcgdev41" />
        <div className="absolute -top-40 left-1/2 h-[620px] w-[900px] -translate-x-1/2 rounded-full blur-[120px]" style={{
        background: 'radial-gradient(circle at 40% 40%, rgba(37,99,246,0.22), transparent 60%), radial-gradient(circle at 65% 55%, rgba(139,92,246,0.20), transparent 62%)'
      }} data-id="element-98" data-mp="sb1ajn1kvv4z" />
      </div>

      <div className="relative mx-auto grid w-full max-w-content grid-cols-1 gap-y-12 px-6 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-x-8 lg:gap-y-0 lg:px-8" data-id="element-99" data-mp="384jsewrv5frv">
        {/* Headline block */}
        <div className="lg:col-start-1 lg:row-start-1" data-id="element-100" data-mp="3o5j4fdov3pne">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease
        }} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 backdrop-blur-sm" data-id="element-101" data-mp="39b4knaxh4c7o">
            <SparklesIcon aria-hidden="true" className="h-3.5 w-3.5 text-[#7fa3ff]" data-id="element-102" data-mp="359sxrd5zi536" />
            <span className="text-[12px] font-light tracking-wide text-white/65 sm:text-[12.5px]" data-id="element-103" data-mp="2qlofgaaufvkx">
              Creamos productos digitales que impulsan negocios
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.08,
          ease
        }} className="mt-6 max-w-[15ch] text-[2.35rem] font-semibold leading-[1.05] tracking-[-0.03em] text-white sm:mt-7 sm:text-6xl lg:text-[4.2rem]" data-id="element-104" data-mp="2hl9s75ebk6es">
            Marketing,{' '}
            <span className="gradient-text" data-id="element-105" data-mp="id9tgcjzi9ta">Inteligencia Artificial</span> y
            Productos Digitales.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.16,
          ease
        }} className="mt-5 max-w-xl text-[15.5px] font-light leading-relaxed text-white/55 sm:mt-7 sm:text-[16.5px]" data-id="element-106" data-mp="deyh9v67vn7w">
            Creamos eBooks, recursos, plantillas y soluciones digitales para
            ayudar a emprendedores y empresas a crecer más rápido.
          </motion.p>
        </div>

        {/* Ecosystem visual — right on desktop, right after the subheadline on mobile */}
        <div className="relative order-2 -mx-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:mx-0 lg:self-center lg:-mr-6 xl:-mr-10" data-id="element-107" data-mp="3hqam6y4cdrp9">
          <div className="mx-auto w-full max-w-[400px] sm:max-w-[540px] lg:max-w-none" data-id="element-108" data-mp="21awbw4il4jeb">
            <HeroEcosystem data-id="element-109" data-mp="218jwnz47on4y" />
          </div>
        </div>

        {/* Actions + proof */}
        <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2" data-id="element-110" data-mp="267wiqhn9dq6h">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.24,
          ease
        }} className="flex flex-col gap-3.5 sm:flex-row sm:items-center lg:mt-12" data-id="element-111" data-mp="2b2ahmu14qffw">
            <a href="#productos" className="group brand-gradient-bg inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white shadow-[0_20px_50px_-18px_rgba(90,80,246,0.85)] transition-all duration-300 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0f]" data-id="element-112" data-mp="2ozz0jwbmzcib">
              Explorar Productos
              <ArrowRightIcon aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-113" data-mp="3vhxgzllx9bzl" />
            </a>
            <a href="#nosotros" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-6 py-3.5 text-sm font-medium text-white/85 backdrop-blur-sm transition-colors duration-300 hover:border-white/25 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30" data-id="element-114" data-mp="9ah1v0a6efwl">
              Conocer Más
            </a>
          </motion.div>

          <motion.dl initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="mt-12 flex flex-wrap gap-x-12 gap-y-7 border-t border-white/[0.07] pt-10 sm:mt-16" data-id="element-115" data-mp="3mnshkdsq2s8g">
            {[{
            k: '+120',
            v: 'Productos entregados'
          }, {
            k: '+3.500',
            v: 'Clientes activos'
          }, {
            k: '4.9/5',
            v: 'Satisfacción'
          }].map(s => <div key={s.v} data-id="element-116" data-mp="zlivnzjoqrhr">
                <dt className="text-2xl font-semibold tracking-tight text-white" data-id="element-117" data-mp="zmasfdp0qv9m">
                  {s.k}
                </dt>
                <dd className="mt-1 text-[12.5px] font-light text-white/45" data-id="element-118" data-mp="1s95ak3yd5ijx">
                  {s.v}
                </dd>
              </div>)}
          </motion.dl>
        </div>
      </div>
    </section>;
}